'use client'

import Link from 'next/link'

import { cn } from '@/lib/utils'

export const FLOW_STEPS = [
    { id: 1, label: 'Describe', href: '/' },
    { id: 2, label: 'Brand', href: '/brand-details' },
    { id: 3, label: 'Style', href: '/options' },
    { id: 4, label: 'Logos', href: '/results' }
] as const

interface ProgressRailProps {
    /** 1 through 4. */
    current: number
    className?: string
}

/**
 * The four steps of the create flow, always visible so nobody loses their place.
 * Completed steps are links back; steps ahead are inert.
 */
export const ProgressRail = ({ current, className }: ProgressRailProps) => {
    return (
        <nav aria-label='Logo creation progress' className={cn('w-full', className)}>
            <ol className='flex items-stretch gap-1 sm:gap-2'>
                {FLOW_STEPS.map((step) => {
                    const done = step.id < current
                    const active = step.id === current

                    const inner = (
                        <>
                            <span
                                className={cn(
                                    'h-px w-full transition-colors duration-200 ease-out',
                                    active ? 'bg-signal' : done ? 'bg-ink' : 'bg-line'
                                )}
                            />
                            <span className='flex items-baseline gap-2 pt-3'>
                                <span
                                    className={cn(
                                        'type-eyebrow tabular-nums',
                                        active ? 'text-signal' : done ? 'text-ink' : 'text-ink-subtle'
                                    )}
                                >
                                    {String(step.id).padStart(2, '0')}
                                </span>
                                <span
                                    className={cn(
                                        'text-small transition-colors duration-200 ease-out',
                                        active
                                            ? 'font-medium text-ink'
                                            : done
                                              ? 'text-ink-muted group-hover:text-ink'
                                              : 'text-ink-subtle'
                                    )}
                                >
                                    {step.label}
                                </span>
                            </span>
                        </>
                    )

                    return (
                        <li key={step.id} className='flex-1'>
                            {done ? (
                                <Link
                                    href={step.href}
                                    className='group flex w-full flex-col rounded-sm'
                                    aria-label={`Back to step ${step.id}, ${step.label}`}
                                >
                                    {inner}
                                </Link>
                            ) : (
                                <div
                                    className='flex w-full flex-col'
                                    aria-current={active ? 'step' : undefined}
                                >
                                    {inner}
                                </div>
                            )}
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

export default ProgressRail
